/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, May 2023
 * @flow
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Grid } from '@material-ui/core';
import { indigo } from '@material-ui/core/colors';
import {
  MoneyOff,
  Money,
  Payment as PaymentIcon,
  PlaylistAddCheck,
  Block
} from '@material-ui/icons';
import Card from '../Card';
import Table from '../Table';
import { getHourIn12HrFormat } from '../../utils';
import PieChart from '../PieChart';
import { backgroundColors, borderColors } from '../../constants';
import HourlySalesTipChart from './HourlySalesTipChart';

const paymentByTenderTypeTableColumns = [
  {
    field: 'tenderTypeName',
    headerName: 'Tender Type',
    flex: 1,
    sortable: true,
  },
  {
    field: 'formattedPaymentAmount',
    headerName: 'Payment',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.paymentAmount - nodeB.data.paymentAmount;
    },
  },
  {
    field: 'formattedTipAmount',
    headerName: 'Tip',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.tipAmount - nodeB.data.tipAmount;
    },
  },
  {
    field: 'formattedTipPercent',
    headerName: 'Tip %',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.tipPercent - nodeB.data.tipPercent;
    },
  },
];

const hourlySalesTipTableColumns = [
  {
    field: 'formattedTime',
    headerName: 'Time',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.time - nodeB.data.time;
    },
  },
  {
    field: 'formattedTipAmount',
    headerName: 'Tip Amount',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.tipAmount - nodeB.data.tipAmount;
    },
  },
  {
    field: 'formattedTipPercent',
    headerName: 'Tip %',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.tipPercent - nodeB.data.tipPercent;
    },
  },
];

const Payment = ({ commonData, hourlySales, paymentByTenderType }) => {
  const { currencyInfo } = useSelector(state => state);
  const { currency } = currencyInfo || {};
  const { totalPaidAmount, totalTipAmount, totalTipPercent, totalPaidCheck, totalUnpaidCheck, totalUnpaidAmount } = commonData;

  const cards = [
    { label: 'Payments', value: `${currency}${totalPaidAmount.toFixed(2)}`, icon: PaymentIcon },
    { label: 'Tip', value: `${currency}${totalTipAmount.toFixed(2)}`, icon: Money },
    { label: 'Tip Percent', value: `${totalTipPercent.toFixed(2)}%`, icon: Money },
    { label: 'Checks Paid', value: totalPaidCheck, icon: PlaylistAddCheck },
    { label: 'Checks Unpaid', value: totalUnpaidCheck, icon: Block },
    { label: 'Unpaid', value: `${currency}${totalUnpaidAmount.toFixed(2)}`, icon: MoneyOff },
  ];

  const paymentByTenderTypeArray = Array.from(paymentByTenderType.values());
  const paymentByTenderTypeTableRows = paymentByTenderTypeArray.map(eachTenderType => ({
    id: eachTenderType.id,
    tenderTypeName: eachTenderType.tenderTypeName,
    paymentAmount: eachTenderType.paymentAmount,
    formattedPaymentAmount: `${currency}${eachTenderType.paymentAmount.toFixed(2)}`,
    tipAmount: eachTenderType.tipAmount,
    formattedTipAmount: `${currency}${eachTenderType.tipAmount.toFixed(2)}`,
    tipPercent: eachTenderType.tipPercent,
    formattedTipPercent: `${eachTenderType.tipPercent.toFixed(2)}%`,
  }));

  const paymentByTenderTypePieChartData = {
    labels: paymentByTenderTypeArray.map(item => item.tenderTypeName || ''),
    datasets: [
      {
        label: 'Payment By Tender Type',
        data: paymentByTenderTypeArray.map(item => item.paymentAmount.toFixed(2)),
        backgroundColor: paymentByTenderTypeArray.map((_, index) => backgroundColors[index % backgroundColors.length]),
        borderColor: paymentByTenderTypeArray.map((_, index) => borderColors[index % borderColors.length]),
        borderWidth: 1,
      },
    ],
  };

  //hourly sales
  const timeSlots = Array.from(hourlySales.keys()).map(hours => getHourIn12HrFormat(hours));
  const hourlySalesData = Array.from(hourlySales.values());
  const hourlySalesTipChartData = {
    labels: timeSlots,
    datasets: [
      {
        type: 'bar',
        label: 'Tip Percent',
        data: hourlySalesData.map(it => (it.totalTipAmount && totalTipAmount) ? (it.totalTipAmount / totalTipAmount) * 100 : 0),
        backgroundColor: 'rgba(53, 162, 235, 1)',
      },
      {
        type: 'line',
        label: 'Tip Amount',
        data: hourlySalesData.map(it => it.totalTipAmount),
        borderColor: 'rgb(255, 99, 132, 1)',
        borderWidth: 1,
        currency: currency,
      },
    ],
  };

  const hourlySalesTipTableRows = hourlySalesData.map(it => {
    const tPercent = (it.totalTipAmount && totalTipAmount)
      ? ((it.totalTipAmount / totalTipAmount) * 100)
      : 0;
    return {
      id: it.id,
      time: it.hour,
      formattedTime: getHourIn12HrFormat(it.hour),
      tipAmount: it.totalTipAmount,
      formattedTipAmount: `${currency}${it.totalTipAmount.toFixed(2)}`,
      tipPercent: tPercent,
      formattedTipPercent: `${tPercent.toFixed(2)}%`,
    }
  });

  return (
    <React.Fragment>
      <Grid container>
        {cards.map((card, i) =>
          <Grid item xs={12} sm={6} md={4} lg={3} key={`key - ${card.label}`}>
            <Card
              label={card.label}
              value={card.value}
              icon={card.icon}
              backgroundColor={indigo[500]}
            />
          </Grid>
        )}
      </Grid>
      <Grid container>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Table
            rowData={paymentByTenderTypeTableRows}
            columnDefs={paymentByTenderTypeTableColumns}
            title={'PAYMENT BY TENDER TYPE'}
          />
        </Grid>
        <Grid item xs={12} sm={9} md={6} lg={6}>
          <PieChart
            data={paymentByTenderTypePieChartData}
            title='PAYMENT BY TENDER TYPE'
            formatter={(value, context) => `${currency}${value}`}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} sm={9} md={6} lg={6}>
          <Table
            rowData={hourlySalesTipTableRows}
            columnDefs={hourlySalesTipTableColumns}
            title={'HOURLY TIP SALES'}
          />
        </Grid>
        <Grid item xs={12} sm={9} md={6} lg={6}>
          <HourlySalesTipChart data={hourlySalesTipChartData} title={'HOURLY TIP SALES'} />
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default Payment;
